import { useMemo } from 'react';

import type {
  ProposalDetail,
  ProposedAction,
} from '@/lib/proposals/core/types';
import { isUserEditableAction } from '@/lib/proposals/registry/utils';
import { isFailureVerdictStale } from '@/lib/proposals/utils/actionStatus';
import {
  getPrimaryAction,
  isGmailSendAction,
} from '@/lib/proposals/utils/helpers';

export interface ExecutionStatus {
  primaryAction: ProposedAction | undefined;
  /** True when a SELECTED action is a gmail send — deselecting the email
   * row(s) makes this false. */
  isEmailAction: boolean;
  actionExecutionSucceeded: boolean;
  actionIsIndeterminate: boolean;
  /** Executed successfully AND the proposal is no longer pending (a pending
   * proposal with an executed action is a reverted partial execution — retry
   * is allowed, so it does not count as "already executed"). */
  actionAlreadyExecuted: boolean;
  actionExecutedAt: string | null | undefined;
  actionExecutionFailed: boolean;
  actionCanRetry: boolean;
  actionPermanentlyFailed: boolean;
  /**
   * Whether the user can edit the primary action by ANY affordance — the shell
   * edit button or one the card owns itself. This is deliberately broader than
   * `isEditableAction` (which answers only "does the shell render a pencil?"):
   * it exists to decide whether suggesting an edit is useful advice, and a
   * card-owned edit control makes that advice just as actionable.
   */
  primaryActionEditable: boolean;
  /** Guidance sentence appended to the failed-execution message. */
  failedExecutionGuidance: string;
}

/**
 * THE derivation of a proposal's primary-action execution status — shared by
 * ExecutionStatusBanners (the content-region already-sent / failed banners)
 * and StickyFooter (approve-button gating, confirmation copy), so the banner
 * a user reads and the button state it explains can never disagree.
 *
 * Semantics preserved verbatim from StickyFooter (task 4 part 2a):
 *
 * A failed execution (status 'error'/'failure') does NOT block retrying
 * UNLESS the error is non-retryable (validation errors that won't change on
 * retry). If the proposal is pending (e.g. reverted after a partial
 * execution timeout), retry is allowed even if the primary action already
 * executed — the executor uses idempotency keys to skip already-succeeded
 * actions safely.
 *
 * `indeterminate` is neither success nor a normal failure: the system cannot
 * tell whether the side effect committed, so the user must verify externally
 * and resolve via the resolve-indeterminate endpoint.
 *
 * A non-retryable verdict describes the exact batch that was attempted (e.g.
 * a CRM cardinality conflict exists only because two actions were in the
 * same batch). Once the user changes the composition — deselects or removes
 * a failed action, adds a new one, or edits one — the verdict no longer
 * applies and retrying is meaningful. `selectedActionIds` is the live
 * checkbox state (ahead of the server round-trip), so unchecking a failed
 * action lifts the gate immediately.
 */
export function useExecutionStatus(
  action: ProposalDetail,
  selectedActionIds?: number[]
): ExecutionStatus {
  const primaryAction = useMemo(() => getPrimaryAction(action), [action]);

  const isEmailAction = useMemo(() => {
    const candidateActions =
      selectedActionIds === undefined
        ? action.actions
        : action.actions.filter(a => selectedActionIds.includes(a.id));
    return candidateActions.some(a => isGmailSendAction(a));
  }, [action.actions, selectedActionIds]);

  const primaryStatus = primaryAction?.execution_result?.status;
  const actionExecutionSucceeded =
    !!primaryAction?.executed_at && primaryStatus === 'success';
  const actionIsIndeterminate =
    !!primaryAction?.executed_at && primaryStatus === 'indeterminate';
  const proposalIsPending = action.resolution === 'pending';
  const actionAlreadyExecuted = actionExecutionSucceeded && !proposalIsPending;
  const actionExecutedAt = primaryAction?.executed_at;
  const actionExecutionFailed =
    !!primaryAction?.executed_at &&
    !actionExecutionSucceeded &&
    !actionIsIndeterminate;
  const isRetryable = primaryAction?.execution_result?.is_retryable !== false;
  const failureVerdictStale = isFailureVerdictStale(
    action.actions ?? [],
    selectedActionIds
  );
  const actionCanRetry =
    actionExecutionFailed && (isRetryable || failureVerdictStale);
  const actionPermanentlyFailed =
    actionExecutionFailed && !isRetryable && !failureVerdictStale;
  // `isUserEditableAction`, NOT `isEditableAction`: the question here is
  // whether the user can change the action at all, not whether the shell
  // renders a pencil for it. Actions whose card owns its own edit control
  // (Gmail compose, ask_user_for_info's inline form, UpdateActionCard's
  // click-to-edit cells, NoteCard's pencil on notes and tasks) are fully
  // editable but answer false to the shell question, and were being told
  // their action "can't be retried" while an edit control sat on screen.
  const primaryActionEditable = primaryAction
    ? isUserEditableAction(primaryAction)
    : false;

  let failedExecutionGuidance: string;
  if (actionCanRetry) {
    failedExecutionGuidance = ' You can try again.';
  } else if (primaryActionEditable) {
    failedExecutionGuidance = ' Edit the action and try again.';
  } else {
    failedExecutionGuidance =
      " This action can't be retried. Dismiss it to continue.";
  }

  return {
    primaryAction,
    isEmailAction,
    actionExecutionSucceeded,
    actionIsIndeterminate,
    actionAlreadyExecuted,
    actionExecutedAt,
    actionExecutionFailed,
    actionCanRetry,
    actionPermanentlyFailed,
    primaryActionEditable,
    failedExecutionGuidance,
  };
}
